import jsCookie from 'js-cookie';
import jwtDecode from 'jwt-decode';

import { pages } from 'constants/pages';
import { permissions } from 'constants/permissions';
import { EActiveDomains } from 'enums/activeDomain';
import { ECookieNames } from 'enums/cookieNames';
import { EHttpMethods } from 'enums/httpMethods';
import { EHttpStatusCodes } from 'enums/httpStatusCodes';
import { EUserRole } from 'enums/user';
import { IPermissionsBySection } from 'interfaces/permissions';
import { ISignInRequest } from 'services/interfaces/request/auth/signInRequest';
import { ISignInRequestBody } from 'services/interfaces/request/auth/signInRequestBody';
import {
  IHttpRequestDetailedError,
  // IHttpRequestError,
} from 'services/interfaces/request/http';

export async function signInClientRequest(
  request: ISignInRequest,
): Promise<void> {
  const body: ISignInRequestBody = {
    email: request.data.email,
    password: request.data.password,
  };

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_AUTH_API_URI}/api/users/login`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
      method: EHttpMethods.POST,
      body: JSON.stringify(body),
      cache: 'no-store',
    },
  );
  if (!response.ok) {
    // const data: IHttpRequestError = await response.json();
    request.functions.onStopLoading();

    const detailedError: IHttpRequestDetailedError = {
      message: 'Invalid credentials',
      type: 'requestError',
    };
    throw detailedError;
  }

  const accessToken: string = await response.json();

  const decodedToken: {
    external_admin_id: string;
    external_admin_role: string;
  } = jwtDecode(accessToken);

  // Prevent B2B from logging in external and vice versa, before set cookie
  if (decodedToken.external_admin_role) {
    switch (request.data.activeDomain) {
      case EActiveDomains.B2B:
        if (decodedToken.external_admin_role !== EUserRole.B2BApplyer) {
          const detailedError: IHttpRequestDetailedError = {
            message: 'User only allowed in Externl Assistance',
            type: 'roleError',
          };
          throw detailedError;
        }
        break;
      case EActiveDomains.External:
        if (decodedToken.external_admin_role === EUserRole.B2BApplyer) {
          const detailedError: IHttpRequestDetailedError = {
            message: 'User only allowed in B2B',
            type: 'roleError',
          };
          throw detailedError;
        }
        break;

      default:
        break;
    }
  }

  if (decodedToken.external_admin_id) {
    jsCookie.set(ECookieNames.AccessToken, accessToken, { expires: 1 });
  }

  const accessPermissions = permissions[decodedToken.external_admin_role];

  function isPermissionSectionKey(
    key: string,
  ): key is keyof IPermissionsBySection {
    return (
      key === 'b2bIntervention' ||
      key === 'europeAssistanceIntervention' ||
      key === 'user'
    );
  }

  const getRedirectUrl = (): string => {
    const sections = [
      'europeAssistanceIntervention',
      'b2bIntervention',
      'user',
    ];

    const readableSection = sections.find(
      section =>
        isPermissionSectionKey(section) && accessPermissions[section]?.canRead,
    );

    if (readableSection) {
      switch (readableSection) {
        case 'b2bIntervention':
          return pages.locale(request.locale).intervention.b2bList;
        case 'europeAssistanceIntervention':
          return pages.locale(request.locale).intervention.externalList;
        case 'user':
          return pages.locale(request.locale).user.list;
        default:
          return pages.locale(request.locale).unauthorized;
      }
    }

    return pages.locale(request.locale).unauthorized;
  };

  const redirectUrl = getRedirectUrl();

  if (response.status === EHttpStatusCodes.Created) {
    request.functions.navigateTo(redirectUrl);
  }

  request.functions.onStopLoading();
}
